import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = heroRef.current?.querySelectorAll(".animate-on-enter");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Hero">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=breathtaking%20modern%20commercial%20building%20lobby%20with%20gleaming%20polished%20marble%20floors%2C%20soaring%20glass%20atrium%20ceiling%2C%20spotless%20pristine%20corporate%20headquarters%20interior%2C%20deep%20blue%20and%20warm%20gold%20luxury%20tones%2C%20premium%20business%20environment%20photography%2C%20prestigious%20commercial%20property%2C%20Seattle%20Washington%20state%20financial%20district%20architecture&width=1920&height=1080&seq=hero_commercial_updated003&orientation=landscape"
          alt="Prime Shine WA commercial cleaning services Seattle"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A44]/92 via-[#0F2A44]/72 to-[#0F2A44]/40" />
      </div>

      <div ref={heroRef} className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="max-w-2xl">
          <div className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-100 mb-6">
            <span className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full">
              <i className="ri-star-fill text-xs" />
              {t("hero_badge")}
            </span>
          </div>

          <h1 className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-200 text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
            {t("hero_headline")}
          </h1>

          <p className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-300 text-white/75 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
            {t("hero_subheadline")}
          </p>

          <div className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-400 flex flex-wrap gap-4 mb-10">
            <button
              onClick={() => navigate("/contact")}
              className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-bold px-8 py-4 rounded-full text-base transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
              style={{ boxShadow: "0 4px 20px rgba(212,175,55,0.35)" }}
            >
              {t("hero_cta_primary")}
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => navigate("/services")}
              className="flex items-center gap-2 bg-transparent border-2 border-white/60 hover:border-white text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 hover:bg-white/10 whitespace-nowrap cursor-pointer"
            >
              {t("hero_cta_secondary")}
            </button>
          </div>

          <div className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-500 flex flex-wrap gap-5">
            {[
              { icon: "ri-shield-check-fill", text: t("hero_trust_1") },
              { icon: "ri-verified-badge-fill", text: t("hero_trust_2") },
              { icon: "ri-star-fill", text: t("hero_trust_3") },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-[#D4AF37] text-base`} />
                </div>
                <span className="text-white/85 text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 delay-600 absolute bottom-12 right-6 hidden lg:block">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4">
            <div className="flex items-center gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-[#D4AF37] text-sm" />
              ))}
            </div>
            <p className="text-white text-sm font-semibold">{t("hero_rating")}</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
